import { Edit, Server, Trash } from "lucide-react";
import { ContainerCard, EmployeeDetails, ModuleTitleCard } from "../../components/ui/cards";
import { useState } from "react";
import { Reactable } from "../../components/ui/reactable";
import { ASSET_EMPLOYEE_AUDIT } from "../../constants/api";
import { useNavigate } from "react-router-dom";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import dayjs from "dayjs";
import { Button } from "../../components/ui/button";

const AssetEmployeeAudit = () => {

    const [assetEmployeeAuditData, setAssetEmployeeAuditData] = useState([])
    const [totalRecords, setTotalRecords] = useState(0)
    let navigate = useNavigate()
    // Permissions
    const {
        add: addPermission,
        edit: editPermission,
        draft: draftPermission,
        active: isActive
    } = checkUserPermissionFunc("Asset Management", "Asset Employee Audit", "SubMenu");

    const assetEmployeeAuditColumn = [
        {
            accessor: "employee_name",
            Header: "Employee",
            sortable: true,
            fixed: true,
            filterable: true,
            maxWidth: "300px",
            Cell: ({ value, row }) => {
                const record = row;

                const employeeDetails = record.employee_details || record.employee?.employee_details || {};

                let empDetails = {
                    id: employeeDetails.employee_id || "-",
                    first_name: employeeDetails.employee_name || value || "-",
                    designation_id: employeeDetails.designation
                        ? {
                            id: employeeDetails.designation.id,
                            designation_name: employeeDetails.designation.name,
                        }
                        : { id: 0, designation_name: "" },
                    department: employeeDetails.department
                        ? {
                            id: employeeDetails.department.id,
                            department_name: employeeDetails.department.name,
                        }
                        : { id: 0, department_name: "" },
                    profile: employeeDetails.profile || "",
                    title: employeeDetails.title || "",
                    employee_status: employeeDetails.employee_status || false
                };

                return <EmployeeDetails details={empDetails} size="xs" />;
            },
        },
        {
            accessor: 'asset_code',
            Header: 'Asset Details',
            Cell: ({ value, row }) => {
                const assetData = row.asset_registry_id;

                return (
                    <div className="flex flex-col space-y-0.5 min-w-[180px]">
                        <div className="flex items-center gap-1">
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                {assetData.asset_code}
                            </span>
                            {/* <span className="text-xs text-gray-500 dark:text-gray-400">
                                id:({assetData.id})
                            </span> */}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 truncate">
                            {assetData.asset_name}
                        </div>
                    </div>
                );
            }
        },
        {
            accessor: "issued_date",
            Header: "Issued Date",
            sortable: true,
            Cell: ({ value }) => value && dayjs(value).isValid() ? dayjs(value).format('DD/MM/YYYY') : "-"
        },
        {
            accessor: "hand_over_by",
            Header: "Handover By",
            sortable: true,
            maxWidth: "300px",
            Cell: ({ value, row }) => {
                const record = row.hand_over_by;

                if (!record) {
                    return <span className="text-gray-400">Not assigned</span>;
                }

                let empDetails = {
                    // id: record.employee_id || record.employee_code || "-", // employee code is not avaiable is hidded
                    first_name: record.first_name || "-",
                    last_name: record.last_name || "",
                    designation_id: {
                        id: 0, // You might not have designation id in this structure
                        designation_name: record.designation_name || ""
                    },
                    department: {
                        id: 0, // You might not have department id in this structure
                        department_name: record.department || ""
                    },
                    profile: record.profile || "",
                    title: record.title || "",
                    employee_status: true // Assuming active if handover exists
                };

                return <EmployeeDetails details={empDetails} size="xs" />;
            },
        },
        {
            accessor: "return_date",
            Header: "Return Date",
            sortable: true,
            Cell: ({ value }) => value && dayjs(value).isValid() ? dayjs(value).format('DD/MM/YYYY') : "-"
        },
        {
            accessor: "handing_over_to",
            Header: "Handover To",
            sortable: true,
            maxWidth: "300px",
            Cell: ({ value, row }) => {
                const record = row.handing_over_to_id;

                if (!record || !record.employee_id || !record.first_name || !record.last_name) {
                    return <span className="text-gray-400">Not assigned</span>;
                }

                let empDetails = {
                    id: record.employee_id || "-",
                    first_name: record.first_name || "-",
                    last_name: record.last_name || "",
                    designation_id: {
                        id: 0,
                        designation_name: record.designation_name || ""
                    },
                    department: {
                        id: 0,
                        department_name: record.department || ""
                    },
                    profile: record.profile || "",
                    title: record.title || "",
                    employee_status: true
                };

                return <EmployeeDetails details={empDetails} size="xs" />;
            },
        },
        ...(editPermission ? [
            {
                accessor: 'action',
                Header: 'Action',
                fixed: true,
                maxWidth: "100px",
                align: "right",
                Cell: ({ value, row }) => {

                    const handleOpenAuditEdit = () => {
                        navigate("/assetmanagement/assetemployeeaudit/add", { state: { fromEdit: true, editDetails: row, isActive: isActive } })
                    }

                    return (
                        <div className="flex items-center gap-2">
                            {/* Edit Button */}
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                                onClick={handleOpenAuditEdit}
                            >
                                <Edit className="h-4 w-4" />
                            </Button>
                        </div>
                    )
                }
            }
        ] : [])
    ]

    const handleOpenAuditEdit = () => {
        navigate("/assetmanagement/assetemployeeaudit/add")
    }

    return (
        <ContainerCard>
            <ModuleTitleCard Icon={Server} title='Asset Employee Audit' subtitle='Track asset assignments and employee handovers' buttonEnable={!!addPermission} buttonName="Add Asset Employee Audit" totalRecords={totalRecords} setOpenDialog={handleOpenAuditEdit} />

            <Reactable
                data={assetEmployeeAuditData}
                setData={setAssetEmployeeAuditData}
                columns={assetEmployeeAuditColumn}
                setTotalCount={setTotalRecords}
                serverSide
                url={ASSET_EMPLOYEE_AUDIT}
                tableHeight="calc(100vh - 320px)"
                isCSVRequired
                isPDFRequired
                isDraftRequired={!!draftPermission}
            />
        </ContainerCard>
    );
}

export default AssetEmployeeAudit;